import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, Rocket, Crosshair, Sparkles, HeartHandshake, Layers, FolderKanban } from "lucide-react";

const BADGE_ICON = { rocket: Rocket, crosshair: Crosshair, sparkles: Sparkles, "heart-handshake": HeartHandshake, layers: Layers };

export default function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [badges, setBadges] = useState({});
  useEffect(() => {
    api.get("/leaderboard").then((r) => setUsers(r.data));
    api.get("/badges").then((r) => setBadges(r.data));
  }, []);

  const medal = ["#F59E0B", "#94A3B8", "#B45309"];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4"><Trophy className="w-6 h-6 text-amber-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">Builder Leaderboard</h1>
        <p className="text-slate-400 mt-1">Top builders ranked by contribution, launches and impact.</p>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        {users.length === 0 && <p className="p-10 text-center text-slate-500">No builders yet — be the first to earn XP.</p>}
        {users.map((u, i) => (
          <div key={u.user_id} className="flex items-center gap-4 p-4 border-b border-white/5 last:border-0 hover:bg-white/2" data-testid={`leaderboard-row-${i}`}>
            <span className="font-mono font-bold text-lg w-8 text-center" style={{ color: i < 3 ? medal[i] : "#64748B" }}>{i + 1}</span>
            <Avatar className="w-10 h-10 border border-white/10"><AvatarImage src={u.picture} /><AvatarFallback className="bg-cyan-500/20 text-cyan-300">{u.name?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-100 truncate">{u.name || "Builder"}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center gap-1"><FolderKanban className="w-3 h-3" />{u.projects} projects</span>
                <span className="flex items-center gap-1"><Rocket className="w-3 h-3" />{u.launches} launches</span>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1">
              {(u.badges || []).slice(0, 4).map((b) => {
                const Icon = BADGE_ICON[badges[b]?.icon] || Sparkles;
                return <span key={b} title={badges[b]?.name} className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-cyan-400" /></span>;
              })}
            </div>
            <span className="font-mono font-bold text-cyan-400 w-16 text-right">{u.xp} XP</span>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h3 className="font-semibold text-slate-200 mb-4">Achievement Badges</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(badges).map(([key, b]) => {
            const Icon = BADGE_ICON[b.icon] || Sparkles;
            return <div key={key} className="glass rounded-xl p-4 flex items-center gap-3"><span className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center"><Icon className="w-4 h-4 text-cyan-400" /></span><span className="text-sm text-slate-300">{b.name}</span></div>;
          })}
        </div>
      </div>
    </div>
  );
}
